import type { InsightsComparisonWindow } from '@/components/ReviewsManagement/InsightsHub/insightsWindow'
import { makeRatioPercent } from '@/helpers/chartFormat'
import { direction, monthLabel } from '@/helpers/insightsMetrics'

import { metricState, requestState, type InsightsMetricState } from './insightsState'
import type { CompetitionInsightsQuery, InsightsProductsBySlugsQuery } from '../../reviews.generated'

export type CompetitionTrendDirection = 'up' | 'down' | 'flat'

export interface CompetitionBenchmarkData {
  values: (number | null)[]
  caption: CompetitionVisibilityCaption
}

export type CompetitionVisibilityRole = 'you' | 'competitor' | 'others'

export interface CompetitionVisibilitySegment {
  role: CompetitionVisibilityRole
  label?: string
  value: number
}

export type CompetitionShareOfVoiceSpan = 'lastMonths' | 'ytd' | 'latest'

export interface CompetitionVisibilityTrend {
  value: string
  direction: CompetitionTrendDirection
}

export interface CompetitionVisibilityCaption {
  span: CompetitionShareOfVoiceSpan
  compared: boolean
  params: Record<string, string | number>
}

export interface CompetitionVisibilityData {
  sharePercent: string
  trend: CompetitionVisibilityTrend | null
  caption: CompetitionVisibilityCaption
  segments: CompetitionVisibilitySegment[]
}

export interface CompetitionCompetitorRow {
  label: string
  value: number
}

export interface CompetitionAttributeItem {
  key: string
  value: number | null
  comparison: number
}

export interface CompetitionPricingOwn {
  priceMonthlyEur: number
  decile: number
}

export interface CompetitionPricingData {
  deciles: number[]
  values: number[]
  productsInCategory: number
  own: CompetitionPricingOwn | null
  ownUnpriced: boolean
  categoryAverageMonthlyEur: number | null
}

export interface CompetitionInsightsView {
  benchmark: CompetitionBenchmarkData
  visibility: CompetitionVisibilityData
  competitors: CompetitionCompetitorRow[]
  competitorsCaption: CompetitionVisibilityCaption
  attributes: CompetitionAttributeItem[]
  pricing: CompetitionPricingData
  states: {
    benchmark: InsightsMetricState
    visibility: InsightsMetricState
    competitors: InsightsMetricState
    attributes: InsightsMetricState
    pricing: InsightsMetricState
  }
}

type CompetitionResults = NonNullable<NonNullable<CompetitionInsightsQuery['manage']>['insights']>['results']
type CompetitionResponse = NonNullable<CompetitionResults[number]['response']>
type BenchmarkResponse = Extract<CompetitionResponse, { __typename?: 'ManageInsightsBenchmarkWindowResponse' }>
type ShareOfVoiceWindowResponse = Extract<CompetitionResponse, { __typename?: 'ManageInsightsShareOfVoiceWindowResponse' }>
type ShareOfVoiceCompetitorsResponse = Extract<CompetitionResponse, { __typename?: 'ManageInsightsShareOfVoiceCompetitorsResponse' }>
type CovisitationResponse = Extract<CompetitionResponse, { __typename?: 'ManageInsightsCovisitationResponse' }>
type AttributeIndexResponse = Extract<CompetitionResponse, { __typename?: 'ManageInsightsAttributeIndexResponse' }>
type PricingLandscapeResponse = Extract<CompetitionResponse, { __typename?: 'ManageInsightsPricingLandscapeResponse' }>
type PricingLandscapeRow = PricingLandscapeResponse['rows'][number]
type PricingPositionResponse = Extract<CompetitionResponse, { __typename?: 'ManageInsightsPricingPositionResponse' }>
type PricingPositionRow = PricingPositionResponse['rows'][number]
type CompetitorProduct = InsightsProductsBySlugsQuery['listProducts'][number]

const PRICING_LANDSCAPE_MIN_PRODUCTS = 20

const round1 = (value: number): number => Math.round(value * 10) / 10

const round1OrNull = (value: number | null | undefined): number | null => (value == null ? null : round1(value))

const pickResponses = (results: CompetitionResults) => {
  let benchmark: BenchmarkResponse | undefined
  let shareOfVoiceWindow: ShareOfVoiceWindowResponse | undefined
  let shareOfVoiceCompetitors: ShareOfVoiceCompetitorsResponse | undefined
  let covisitation: CovisitationResponse | undefined
  let attributes: AttributeIndexResponse | undefined
  let pricingLandscape: PricingLandscapeResponse | undefined
  let pricingPosition: PricingPositionResponse | undefined
  for (const item of results) {
    const response = item.response
    if (!response || item.status !== 200) continue
    if (response.__typename === 'ManageInsightsBenchmarkWindowResponse') benchmark = response
    else if (response.__typename === 'ManageInsightsShareOfVoiceWindowResponse') shareOfVoiceWindow = response
    else if (response.__typename === 'ManageInsightsShareOfVoiceCompetitorsResponse') shareOfVoiceCompetitors = response
    else if (response.__typename === 'ManageInsightsCovisitationResponse') covisitation = response
    else if (response.__typename === 'ManageInsightsAttributeIndexResponse') attributes = response
    else if (response.__typename === 'ManageInsightsPricingLandscapeResponse') pricingLandscape = response
    else if (response.__typename === 'ManageInsightsPricingPositionResponse') pricingPosition = response
  }
  return {
    benchmark,
    shareOfVoiceWindow,
    shareOfVoiceCompetitors,
    covisitation,
    attributes,
    pricingLandscape,
    pricingPosition,
  }
}

export const visibilityCaption = (window: InsightsComparisonWindow, compared: boolean, locale: string): CompetitionVisibilityCaption => {
  const month = (period: string | null) => monthLabel(period, locale, 'long')
  if (window.span === 'lastMonths') return { span: window.span, compared, params: { count: window.months } }
  if (window.span === 'latest') {
    return { span: window.span, compared, params: { month: month(window.start), previousMonth: month(window.previousStart) } }
  }
  const year = Number(window.start.slice(0, 4))
  return {
    span: window.span,
    compared,
    params: { startMonth: month(window.start), endMonth: month(window.end), year, previousYear: year - 1 },
  }
}

const buildBenchmark = (
  benchmark: BenchmarkResponse | undefined,
  window: InsightsComparisonWindow,
  locale: string,
): CompetitionBenchmarkData => {
  const row = benchmark?.rows[0]
  return {
    values: [
      round1OrNull(row?.toolPageviews),
      round1OrNull(row?.top3AvgPageviews),
      round1OrNull(row?.top10AvgPageviews),
      round1OrNull(row?.categoryAvgPageviews),
    ],
    caption: visibilityCaption(window, false, locale),
  }
}

const shownCompetitors = (competitors: ShareOfVoiceCompetitorsResponse | undefined, productSlug: string) =>
  (competitors?.rows ?? []).filter((row): row is typeof row & { toolSlug: string } => !!row.toolSlug && row.toolSlug !== productSlug)

const buildVisibility = (
  own: ShareOfVoiceWindowResponse | undefined,
  competitors: ShareOfVoiceCompetitorsResponse | undefined,
  {
    productSlug,
    titleBySlug,
    window,
    locale,
  }: { productSlug: string; titleBySlug: Map<string, string>; window: InsightsComparisonWindow; locale: string },
): { visibility: CompetitionVisibilityData; hasShare: boolean } => {
  const row = own?.rows[0]
  const share = row?.sovPageviews ?? null
  const previous = row?.previousComplete ? (row.previousSovPageviews ?? null) : null
  const deltaPp = previous === null || share === null ? null : round1((share - previous) * 100)
  const category = row?.categoryPageviews ?? 0
  const ofCategory = (pageviews: number) => (category > 0 ? pageviews / category : 0)
  const ownPageviews = row?.toolPageviews ?? 0
  const shown = shownCompetitors(competitors, productSlug)
  const shownPageviews = shown.reduce((sum, competitor) => sum + (competitor.toolPageviews ?? 0), 0)
  const signed = new Intl.NumberFormat(locale, { signDisplay: 'exceptZero', minimumFractionDigits: 1, maximumFractionDigits: 1 })
  return {
    visibility: {
      sharePercent: makeRatioPercent(locale, 1)(share ?? 0),
      trend: deltaPp === null ? null : { value: signed.format(deltaPp), direction: direction(deltaPp) },
      caption: visibilityCaption(window, deltaPp !== null, locale),
      segments: [
        { role: 'you', value: ofCategory(ownPageviews) },
        ...shown.map((competitor) => ({
          role: 'competitor' as const,
          label: titleBySlug.get(competitor.toolSlug) ?? competitor.toolSlug,
          value: ofCategory(competitor.toolPageviews ?? 0),
        })),
        { role: 'others', value: ofCategory(Math.max(0, category - ownPageviews - shownPageviews)) },
      ],
    },
    hasShare: share !== null,
  }
}

const topCompetitors = (
  covisitation: CovisitationResponse | undefined,
  window: InsightsComparisonWindow,
): { slug: string; value: number }[] => {
  const rows = (covisitation?.rows ?? []).filter((row) => row.period && row.period >= window.start && row.period <= window.end)
  const comparingByMonth = new Map<string, number>()
  const sessionsByCompetitor = new Map<string, number>()
  for (const row of rows) {
    if (row.period && row.comparingSessions != null && !comparingByMonth.has(row.period)) {
      comparingByMonth.set(row.period, row.comparingSessions)
    }
    if (row.competitorSlug && row.covisitingSessions) {
      sessionsByCompetitor.set(row.competitorSlug, (sessionsByCompetitor.get(row.competitorSlug) ?? 0) + row.covisitingSessions)
    }
  }
  const comparing = [...comparingByMonth.values()].reduce((sum, value) => sum + value, 0)
  if (!comparing) return []
  return [...sessionsByCompetitor.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 5)
    .map(([slug, sessions]) => ({ slug, value: sessions / comparing }))
}

export const competitionCompetitorSlugs = (
  query: CompetitionInsightsQuery | null | undefined,
  window: InsightsComparisonWindow,
  productSlug: string,
): string[] => {
  const { covisitation, shareOfVoiceCompetitors } = pickResponses(query?.manage?.insights?.results ?? [])
  const slugs = [
    ...topCompetitors(covisitation, window).map(({ slug }) => slug),
    ...shownCompetitors(shareOfVoiceCompetitors, productSlug).map(({ toolSlug }) => toolSlug),
  ]
  return [...new Set(slugs)]
}

const buildCompetitors = (
  covisitation: CovisitationResponse | undefined,
  window: InsightsComparisonWindow,
  titleBySlug: Map<string, string>,
): CompetitionCompetitorRow[] => {
  return topCompetitors(covisitation, window).map(({ slug, value }) => ({ label: titleBySlug.get(slug) ?? slug, value }))
}

interface PricedDecile {
  decile: number
  price: number
  min: number
  max: number
}

const nearestDecile = (price: number, deciles: PricedDecile[]): number | undefined => {
  let nearest: { decile: number; distance: number } | undefined
  for (const { decile, min, max } of deciles) {
    const distance = Math.max(min - price, price - max, 0)
    if (!nearest || distance < nearest.distance) nearest = { decile, distance }
  }
  return nearest?.decile
}

const ownPrice = (
  row: PricingPositionRow | undefined,
  landscapeCategory: string | undefined,
  deciles: PricedDecile[],
): CompetitionPricingOwn | null => {
  if (row?.entryPriceMonthlyEur == null) return null
  const decile = row.primaryCategorySlug === landscapeCategory ? row.priceDecile : nearestDecile(row.entryPriceMonthlyEur, deciles)
  return decile == null ? null : { priceMonthlyEur: row.entryPriceMonthlyEur, decile }
}

const categoryAverage = (rows: PricingLandscapeRow[]): number | null => {
  let weightedSum = 0
  let products = 0
  for (const row of rows) {
    if (row.avgEntryPriceMonthlyEur == null) continue
    const weight = row.productsInDecile ?? 1
    weightedSum += row.avgEntryPriceMonthlyEur * weight
    products += weight
  }
  return products > 0 ? weightedSum / products : null
}

const buildPricing = (
  landscape: PricingLandscapeResponse | undefined,
  position: PricingPositionResponse | undefined,
): CompetitionPricingData => {
  const rows = landscape?.rows ?? []
  const ownRow = position?.rows[0]
  const pricedDeciles: PricedDecile[] = rows
    .flatMap((row) =>
      row.priceDecile == null || row.avgEntryPriceMonthlyEur == null
        ? []
        : [
            {
              decile: row.priceDecile,
              price: row.avgEntryPriceMonthlyEur,
              min: row.minEntryPriceMonthlyEur ?? row.avgEntryPriceMonthlyEur,
              max: row.maxEntryPriceMonthlyEur ?? row.avgEntryPriceMonthlyEur,
            },
          ],
    )
    .sort((a, b) => a.decile - b.decile)
  return {
    deciles: pricedDeciles.map(({ decile }) => decile),
    values: pricedDeciles.map(({ price }) => price),
    productsInCategory: rows[0]?.productsInCategoryCount ?? 0,
    own: ownPrice(ownRow, landscape?.entity?.id, pricedDeciles),
    ownUnpriced: ownRow != null && ownRow.entryPriceMonthlyEur == null,
    categoryAverageMonthlyEur: categoryAverage(rows),
  }
}

export const mapCompetitionInsights = (
  query: CompetitionInsightsQuery | null | undefined,
  {
    productSlug,
    products,
    locale,
    comparisonWindow,
  }: { productSlug: string; products: CompetitorProduct[]; locale: string; comparisonWindow: InsightsComparisonWindow },
): CompetitionInsightsView => {
  const results = query?.manage?.insights?.results ?? []
  const { benchmark, shareOfVoiceWindow, shareOfVoiceCompetitors, covisitation, attributes, pricingLandscape, pricingPosition } =
    pickResponses(results)
  const byMetric = new Map(results.map((item) => [item.metric, item]))

  const pricing = buildPricing(pricingLandscape, pricingPosition)
  const titleBySlug = new Map(products.map((product) => [product.slug, product.title]))
  const { visibility, hasShare } = buildVisibility(shareOfVoiceWindow, shareOfVoiceCompetitors, {
    productSlug,
    titleBySlug,
    window: comparisonWindow,
    locale,
  })
  const competitors = buildCompetitors(covisitation, comparisonWindow, titleBySlug)

  return {
    benchmark: buildBenchmark(benchmark, comparisonWindow, locale),
    visibility,
    competitors,
    competitorsCaption: visibilityCaption(comparisonWindow, false, locale),
    attributes: (attributes?.rows ?? []).map((row) => ({
      key: row.attribute ?? '',
      value: row.positiveShare ?? null,
      comparison: row.categoryAvgPositiveShare ?? 0,
    })),
    pricing,
    states: {
      benchmark: metricState(byMetric.get('benchmark-window'), benchmark?.rows.length ?? 0),
      visibility:
        requestState(byMetric.get('share-of-voice-competitors')) === 'error'
          ? 'error'
          : metricState(byMetric.get('share-of-voice-window'), hasShare ? 1 : 0),
      competitors: covisitation?.truncated ? 'error' : metricState(byMetric.get('covisitation'), competitors.length),
      attributes: metricState(byMetric.get('attribute-index'), attributes?.rows.length ?? 0),
      pricing: metricState(
        byMetric.get('pricing-landscape'),
        pricing.productsInCategory >= PRICING_LANDSCAPE_MIN_PRODUCTS ? pricing.values.length : 0,
      ),
    },
  }
}
